import type { JSX, ReactElement } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { AggregatedStat, IHosts, TAdChannelKey } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import { TableStatsCard, Text, Well } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import { FunnelChart } from '../FunnelChart';
import type { IData } from '../FunnelChart/FunnelChart';

import * as styles from './ActivityBreakdownCard.css';

interface ICardProps {
  /**
   * Active channel filter on Omni campaign reports. When `'meta'`, the
   * domain-breakdown bar chart is replaced by an empty state because Meta
   * does not expose host-level activity data. `undefined` for Overview /
   * non-Omni reports.
   */
  activeChannel?: TAdChannelKey;
  aggregatedStats: AggregatedStat;
  conversions: number;
  /**
   * Host-level activity data driving the bar chart. Decoupled from
   * `aggregatedStats` so the caller can decide which channel's hosts to
   * show (the backend currently only emits hosts at the top level).
   */
  hosts?: IHosts;
  /**
   * Force the Meta empty state regardless of `activeChannel`. Set on Meta-only
   * Pixl+ (Omni) campaigns: those reports never render channel tabs (because
   * tabs require both Display + Meta integrations), so `activeChannel` stays
   * `undefined` even though the only data available is Meta's, which carries
   * no host-level breakdown.
   */
  isMetaOnly?: boolean;
}

interface IHostDatum {
  Clicks: number;
  Impressions: number;
}

interface IHostData {
  [key: string]: IHostDatum;
}

const domainOptions = [
  ['google', 'Google'],
  ['doubleclick', 'Google'],
  ['yahoo', 'Yahoo'],
  ['adnxs', 'AdNexus Media'],
  ['atwola', 'AOL Network'],
  ['aol', 'AOL Network'],
  ['adtechus', 'AOL Network'],
  ['zynga', 'Zynga Game Network'],
  ['intermarket', 'Intermarket Network'],
];

function maskDomain(domain: string): string {
  const match = domainOptions.find((option) => domain.includes(option[0]));
  if (match) {
    return match[1];
  }
  return domain;
}

interface ITickProps {
  payload: { value: string };
  x: number;
  y: number;
}

function DomainTick(props: Readonly<ITickProps>): ReactElement<SVGElement> {
  const { x, y, payload } = props;
  let label = payload.value;
  if (label.length >= 20) {
    label = `${label.slice(0, 17)}...`;
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={2.5}
        fill={cssVar('--color-text-body')}
        fontSize={10}
        textAnchor={'end'}
        width={100}
        x={0}
        y={0}
      >
        {label}
      </text>
    </g>
  );
}

function sumHostActivity(statHosts: IHosts, flavor: keyof IHosts, hosts: IHostData): void {
  const flavorHosts = statHosts[flavor];
  if (flavorHosts) {
    Object.entries(flavorHosts).forEach(([key, value]) => {
      const domain = maskDomain(key);
      if (flavor.includes('view')) {
        if (hosts[domain]) {
          hosts[domain].Impressions += value;
        } else {
          hosts[domain] = {
            Impressions: value,
            Clicks: 0,
          };
        }
      } else if (flavor.includes('click')) {
        if (hosts[domain]) {
          hosts[domain].Clicks += value;
        } else {
          hosts[domain] = {
            Impressions: 0,
            Clicks: value,
          };
        }
      }
    });
  }
}

export function ActivityBreakdownCard({
  activeChannel,
  aggregatedStats,
  conversions,
  hosts,
  isMetaOnly,
}: Readonly<ICardProps>): JSX.Element {
  const { t } = useTranslation();
  const hostsByDomain: IHostData = {};
  const views = aggregatedStats.get('num_views', 0);
  const clicks = aggregatedStats.get('num_clicks', 0);
  if (hosts) {
    (
      [
        'ad_view',
        'page_view',
        'email_view',
        'ad_click',
        'page_link_click',
        'email_link_click',
      ] as Array<keyof IHosts>
    ).forEach((flavor) => {
      sumHostActivity(hosts, flavor, hostsByDomain);
    });
  }

  const chartData = Object.entries(hostsByDomain)
    .sort((a, b) => b[1].Impressions - a[1].Impressions)
    .slice(0, 10)
    .map(([domain, { Impressions, Clicks }]) => {
      return {
        domain: domain.split('_').join('.'),
        Impressions,
        Clicks,
      };
    });

  const funnelChartData = ((): IData[] => {
    const funnelChartData: Array<[number, string]> = [
      [views, 'Views'],
      [clicks, 'Clicks'],
      [conversions, 'Conversions'],
    ];
    return funnelChartData
      .filter(([value]) => value > 0)
      .map(([value, label]) => ({ value, label }));
  })();

  return (
    <TableStatsCard title={reportModuleLabels.includeActivityBreakdown}>
      <div className={styles.wrapper}>
        <Well
          className={styles.chartSlot}
          layout={'vertical'}
          name={'activity_breakdown_chart_slot'}
          theme={'neutral'}
          width={'full'}
        >
          {activeChannel === 'meta' || isMetaOnly ? (
            <div className={styles.emptyContent}>
              <Text.SmallMedium>{t('No data available')}</Text.SmallMedium>
              <Text.SmallRegular>
                {t("Meta doesn't share domain-level activity data")}
              </Text.SmallRegular>
            </div>
          ) : (
            <ResponsiveContainer aspect={1.25} width={'100%'}>
              <BarChart data={chartData} height={400} layout={'vertical'}>
                <CartesianGrid strokeDasharray={'3 3'} />
                <YAxis dataKey={'domain'} tick={DomainTick} type={'category'} width={100} />
                <XAxis type={'number'} />
                <Tooltip />
                <Bar dataKey={'Impressions'} fill={cssVar('--color-primary-3')} stackId={'a'} />
                <Bar dataKey={'Clicks'} fill={cssVar('--color-brand-orange')} stackId={'a'} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Well>
        <Well
          className={styles.chartSlot}
          layout={'vertical'}
          name={'activity_breakdown_funnel_slot'}
          theme={'neutral'}
          width={'full'}
        >
          <ResponsiveContainer aspect={1.25} width={'100%'}>
            <FunnelChart data={funnelChartData} height={400} width={500} />
          </ResponsiveContainer>
        </Well>
      </div>
    </TableStatsCard>
  );
}
